<template>
  <div>
    <PTransparentLoader :loading="loading">
      <CRow v-if="payout">
        <CCol lg="12">
          <div class="d-flex align-items-center mb-4">
            <CButton
              color="link"
              class="p-0 mr-3"
              @click="goBack"
            >
              <CIcon name="cil-arrow-left" size="lg"/>
            </CButton>
            <h1 class="main-header mb-0">
              <translate translate-context="admin-payout">Payout</translate>
              #{{ payout.id }}
            </h1>
            <CBadge :color="getStatusColor(payout.status)" class="ml-3">
              {{ getStatusLabel(payout.status) }}
            </CBadge>
            <CButton
              v-if="canDownload"
              color="primary"
              size="sm"
              variant="outline"
              class="ml-auto"
              @click="handleDownloadExcel"
            >
              <CIcon name="cil-cloud-download" size="sm" class="mr-1"/>
              <translate translate-context="admin-payout">Download Excel</translate>
            </CButton>
          </div>

          <!-- Payout Information -->
          <CCard class="mb-3">
            <CCardHeader>
              <translate translate-context="admin-payout">Payout Information</translate>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Contract Manager</translate>
                    </small>
                    <strong>{{ payout.contract_manager_name }}</strong>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Created At</translate>
                    </small>
                    {{ formatDate(payout.created_at) }}
                  </div>
                </CCol>
                <CCol md="3" v-if="payout.date_from || payout.date_to">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Period</translate>
                    </small>
                    {{ formatDate(payout.date_from) || '-' }} - {{ formatDate(payout.date_to) || '-' }}
                  </div>
                </CCol>
                <CCol md="3" v-if="payout.paid_at">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Paid At</translate>
                    </small>
                    {{ formatDate(payout.paid_at) }}
                    <span v-if="payout.paid_by_name" class="text-muted">
                      ({{ payout.paid_by_name }})
                    </span>
                  </div>
                </CCol>
              </CRow>

              <CRow v-if="payout.contact_user_email">
                <CCol md="6">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Contact User</translate>
                    </small>
                    {{ payout.contact_user_name || '' }} &lt;{{ payout.contact_user_email }}&gt;
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Amounts -->
          <CCard class="mb-3">
            <CCardHeader>
              <translate translate-context="admin-payout">Amounts</translate>
            </CCardHeader>
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Gross Amount</translate>
                    </small>
                    <strong>{{ formatAmount(payout.paid_gross) }} {{ payout.currency }}</strong>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Refunds</translate>
                    </small>
                    <span class="text-danger">-{{ formatAmount(payout.refunds) }} {{ payout.currency }}</span>
                  </div>
                </CCol>
                <CCol md="3">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Net Amount</translate>
                    </small>
                    <strong class="text-success">{{ formatAmount(payout.paid_net) }} {{ payout.currency }}</strong>
                  </div>
                </CCol>
              </CRow>

              <CRow>
                <CCol md="3" v-if="payout.iban">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">IBAN</translate>
                    </small>
                    <code>{{ payout.iban }}</code>
                  </div>
                </CCol>
                <CCol md="3" v-if="payout.invoice_number">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Invoice Number</translate>
                    </small>
                    {{ payout.invoice_number }}
                  </div>
                </CCol>
                <CCol md="3" v-if="payout.invoice">
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Invoice</translate>
                    </small>
                    <a :href="payout.invoice" target="_blank" class="btn btn-sm btn-outline-primary">
                      <CIcon name="cil-cloud-download" class="mr-1"/>
                      <translate translate-context="admin-payout">Download PDF</translate>
                    </a>
                  </div>
                </CCol>
              </CRow>

              <CRow v-if="payout.notes">
                <CCol>
                  <div class="mb-3">
                    <small class="text-muted d-block">
                      <translate translate-context="admin-payout">Notes</translate>
                    </small>
                    <span class="text-muted">{{ payout.notes }}</span>
                  </div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Pica Services Breakdown -->
          <CCard class="mb-3" v-if="payout.payout_pica_services && payout.payout_pica_services.length > 0">
            <CCardHeader>
              <translate translate-context="admin-payout">Events Breakdown</translate>
            </CCardHeader>
            <CCardBody class="px-0">
              <CDataTable
                :items="payout.payout_pica_services"
                :fields="picaServiceFields"
                :hover="false"
                :bordered="true"
                size="sm"
                class="custom-table custom-table--clean mb-0"
              >
                <template #paid_gross="{item}">
                  <td class="text-right">
                    {{ formatAmount(item.paid_gross) }}
                  </td>
                </template>
                <template #refunds="{item}">
                  <td class="text-right text-danger">
                    <span v-if="item.refunds && parseFloat(item.refunds) > 0">
                      -{{ formatAmount(item.refunds) }}
                    </span>
                    <span v-else>---</span>
                  </td>
                </template>
                <template #paid_net="{item}">
                  <td class="text-right text-success">
                    {{ formatAmount(item.paid_net) }}
                  </td>
                </template>
                <template #download="{item}">
                  <td class="text-center">
                    <CButton
                      color="primary"
                      size="sm"
                      variant="ghost"
                      @click="handleDownloadPicaService(item)"
                      v-c-tooltip="$pgettext('admin-payout', 'Download Excel')"
                    >
                      <CIcon name="cil-cloud-download"/>
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </CCardBody>
          </CCard>

          <!-- Ledger Entries -->
          <CCard class="mb-3" v-if="payout.ledger_entries && payout.ledger_entries.length > 0">
            <CCardHeader>
              <translate translate-context="admin-payout">Ledger Entries</translate>
            </CCardHeader>
            <CCardBody>
              <LedgerEntriesTable :entries="payout.ledger_entries"/>
            </CCardBody>
          </CCard>

          <!-- Invoice Rejected Reason -->
          <CCard class="mb-3" v-if="payout.status === 'invoice_rejected' && payout.invoice_rejected_reason">
            <CCardBody>
              <div class="d-flex align-items-start">
                <CIcon name="cilXCircle" class="text-danger mr-2 mt-1"/>
                <div>
                  <strong class="text-danger">
                    <translate translate-context="admin-payout">Invoice Rejected</translate>
                  </strong>
                  <p class="mb-0 mt-1">{{ payout.invoice_rejected_reason }}</p>
                </div>
              </div>
            </CCardBody>
          </CCard>

          <!-- Actions -->
          <CCard class="mb-3" v-if="(canMarkPaid || canRejectInvoice) && payout.status === 'invoice_uploaded'">
            <CCardBody>
              <div class="d-flex">
                <CButton
                  v-if="canMarkPaid"
                  color="success"
                  @click="handleMarkPayoutAsPaid()"
                  class="mr-2"
                >
                  <CIcon name="cil-check" class="mr-1"/>
                  <translate translate-context="admin-payout">Mark as Paid</translate>
                </CButton>
                <CButton
                  v-if="canRejectInvoice"
                  color="danger"
                  variant="outline"
                  @click="openRejectModal()"
                >
                  <CIcon name="cilXCircle" class="mr-1"/>
                  <translate translate-context="admin-payout">Reject Invoice</translate>
                </CButton>
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </PTransparentLoader>
    <RejectInvoiceModal ref="rejectInvoiceModal" @rejected="onInvoiceRejected"/>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import LedgerEntriesTable from '@/domain/accounting/components/LedgerEntriesTable.vue'
import RejectInvoiceModal from './RejectInvoiceModal.vue'
import { PAYOUT_STATUS, PAYOUT_STATUS_LABELS, PAYOUT_STATUS_COLORS } from './constants'

export default {
  name: 'AdminPayoutDetail',
  components: {
    PTransparentLoader,
    LedgerEntriesTable,
    RejectInvoiceModal
  },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters('adminPayout', ['payout']),
    ...mapGetters('login', ['hasAnyPerms']),

    canMarkPaid() {
      return this.hasAnyPerms(['admin.payouts.mark_paid'])
    },
    canRejectInvoice() {
      return this.hasAnyPerms(['admin.payouts.reject_invoice', 'admin.payouts.mark_paid'])
    },
    canDownload() {
      return this.hasAnyPerms(['admin.payouts.download'])
    },

    picaServiceFields() {
      return [
        { key: 'name', label: this.$pgettext('admin-payout', 'Event') },
        { key: 'paid_gross', label: this.$pgettext('admin-payout', 'Gross') },
        { key: 'refunds', label: this.$pgettext('admin-payout', 'Refunds') },
        { key: 'paid_net', label: this.$pgettext('admin-payout', 'Net') },
        { key: 'download', label: '', _style: 'width: 60px;', sorter: false, filter: false }
      ]
    }
  },
  methods: {
    ...mapActions('adminPayout', ['fetchAdminPayoutDetail', 'markPayoutAsPaid', 'downloadPayoutExcel', 'downloadPicaServiceExcel']),
    ...mapActions('login', ['fetchConfig']),

    getStatusLabel(status) {
      return PAYOUT_STATUS_LABELS[status] || status
    },

    getStatusColor(status) {
      return PAYOUT_STATUS_COLORS[status] || 'light'
    },

    async handleMarkPayoutAsPaid() {
      const confirmed = await this.confirm(
        this.$pgettext('admin-payout', 'Mark as Paid'),
        this.$pgettext('admin-payout', 'Are you sure you want to mark this payout as paid?')
      )
      if (!confirmed) return

      try {
        await this.markPayoutAsPaid({ id: this.$route.params.id, data: {} })
        await this.fetchAdminPayoutDetail(this.$route.params.id)
        this.notifySuccess(this.$pgettext('admin-payout', 'Payout marked as paid successfully'))
      } catch (error) {
        this.notifyError(this.$pgettext('admin-payout', 'Error marking payout as paid'))
      }
    },

    openRejectModal() {
      this.$refs.rejectInvoiceModal.open(this.$route.params.id)
    },

    async onInvoiceRejected() {
      await this.fetchAdminPayoutDetail(this.$route.params.id)
    },

    async handleDownloadPicaService(psItem) {
      try {
        await this.downloadPicaServiceExcel({ payoutId: this.$route.params.id, psId: psItem.pica_service_id })
      } catch (error) {
        this.notifyError(this.$pgettext('admin-payout', 'Error downloading Excel'))
      }
    },

    async handleDownloadExcel() {
      try {
        await this.downloadPayoutExcel(this.$route.params.id)
        this.notifySuccess(this.$pgettext('admin-payout', 'Excel downloaded successfully'))
      } catch (error) {
        this.notifyError(this.$pgettext('admin-payout', 'Error downloading Excel'))
      }
    },

    formatDate(dateString) {
      if (!dateString) return null
      return new Date(dateString).toLocaleString()
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    },

    goBack() {
      if (this.$route.params.contractManagerId) {
        this.$router.push({name: 'contractManager.payouts', params: {contractManagerId: this.$route.params.contractManagerId}})
      } else {
        this.$router.push({name: 'adminPayoutList'})
      }
    }
  },
  async mounted() {
    await this.fetchConfig({ type: 'admin_payouts' })

    this.loading = true
    try {
      await this.fetchAdminPayoutDetail(this.$route.params.id)
    } catch (error) {
      this.notifyError(this.$pgettext('admin-payout', 'Error loading payout details'))
      this.goBack()
    } finally {
      this.loading = false
    }
  }
}
</script>
